export type AudioSample = {
  rms: number;
  peak: number;
};

export type AudioEngineHandle = {
  stop: () => Promise<void>;
  switchDevice: (deviceId: string) => Promise<void>;
  listInputDevices: () => Promise<MediaDeviceInfo[]>;
  getCurrentDeviceId: () => string | undefined;
};

export type AudioEngineOptions = {
  deviceId?: string;
  intervalMs?: number;
  onSample: (s: AudioSample) => void;
  onDeviceLost?: () => void;
};

const FLOOR_DB = -100;

export async function startAudioEngine(
  opts: AudioEngineOptions,
): Promise<AudioEngineHandle> {
  const intervalMs = opts.intervalMs ?? 33;
  let stream: MediaStream | null = null;
  let ctx: AudioContext | null = null;
  let source: MediaStreamAudioSourceNode | null = null;
  let analyser: AnalyserNode | null = null;
  let buf: Float32Array<ArrayBuffer> | null = null;
  let timer: number | null = null;
  let currentDeviceId: string | undefined = opts.deviceId;
  let trackEndedHandler: (() => void) | null = null;

  async function attach(deviceId?: string) {
    await detach();

    const constraints: MediaStreamConstraints = {
      audio: {
        deviceId: deviceId ? { exact: deviceId } : undefined,
        echoCancellation: false,
        noiseSuppression: false,
        autoGainControl: false,
        channelCount: 1,
      },
      video: false,
    };

    stream = await navigator.mediaDevices.getUserMedia(constraints);

    const track = stream.getAudioTracks()[0];
    currentDeviceId =
      track?.getSettings().deviceId ?? deviceId ?? currentDeviceId;

    trackEndedHandler = () => {
      opts.onDeviceLost?.();
    };
    track?.addEventListener("ended", trackEndedHandler);

    ctx =
      ctx ??
      new (window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext })
          .webkitAudioContext)({
        latencyHint: "interactive",
      });

    if (ctx.state === "suspended") {
      await ctx.resume();
    }

    source = ctx.createMediaStreamSource(stream);
    analyser = ctx.createAnalyser();
    analyser.fftSize = 2048;
    analyser.smoothingTimeConstant = 0;
    source.connect(analyser);

    buf = new Float32Array(new ArrayBuffer(analyser.fftSize * 4));

    timer = window.setInterval(tick, intervalMs);
  }

  function tick() {
    if (!analyser || !buf) return;
    analyser.getFloatTimeDomainData(buf);

    let sumSquares = 0;
    let peak = 0;
    for (let i = 0; i < buf.length; i++) {
      const v = buf[i];
      sumSquares += v * v;
      const a = v < 0 ? -v : v;
      if (a > peak) peak = a;
    }
    const rmsLin = Math.sqrt(sumSquares / buf.length);
    opts.onSample({
      rms: toDb(rmsLin),
      peak: toDb(peak),
    });
  }

  async function detach() {
    if (timer !== null) {
      window.clearInterval(timer);
      timer = null;
    }
    if (stream) {
      const track = stream.getAudioTracks()[0];
      if (track && trackEndedHandler) {
        track.removeEventListener("ended", trackEndedHandler);
      }
      for (const t of stream.getTracks()) t.stop();
      stream = null;
    }
    if (source) {
      try {
        source.disconnect();
      } catch {
        // ignore
      }
      source = null;
    }
    analyser = null;
    buf = null;
    trackEndedHandler = null;
  }

  await attach(opts.deviceId);

  return {
    async stop() {
      await detach();
      if (ctx) {
        try {
          await ctx.close();
        } catch {
          // ignore
        }
        ctx = null;
      }
    },
    async switchDevice(deviceId: string) {
      await attach(deviceId);
    },
    async listInputDevices() {
      const devices = await navigator.mediaDevices.enumerateDevices();
      return devices.filter((d) => d.kind === "audioinput");
    },
    getCurrentDeviceId() {
      return currentDeviceId;
    },
  };
}

function toDb(linear: number): number {
  if (linear <= 0) return FLOOR_DB;
  const db = 20 * Math.log10(linear);
  return db < FLOOR_DB ? FLOOR_DB : db;
}
